import React, { useState, useEffect } from 'react';
import lessonService from '../../services/teacher/lessonService';

export default function DashboardContent({ teacherData, setCurrentPage }) {
  const [stats, setStats] = useState({
    totalLessons: 0,
    totalStudents: 0,
    todayLessons: 0,
    monthlyLessons: 0
  });
  const [earnings, setEarnings] = useState({
    summary: {
      toplam_ders: 0,
      toplam_hakedis: 0,
      bu_ay_hakedis: 0,
      gecen_ay_hakedis: 0
    },
    durationDistribution: [],
    monthlyTrend: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      
      // Dersleri getir
      const lessonsData = await lessonService.getAllLessons();
      
      // Öğrencileri getir
      const studentsData = await lessonService.getTeacherStudents();
      
      // Hakediş özetini getir
      try {
        const earningsData = await lessonService.getEarningsSummary();
        setEarnings(earningsData);
      } catch (err) {
        console.error('Hakediş özeti yüklenemedi:', err);
      }
      
      // Bugünün tarihini al
      const today = new Date().toISOString().split('T')[0];
      
      // Bugünkü dersleri filtrele
      const todayLessons = lessonsData.filter(lesson => {
        const lessonDate = new Date(lesson.baslangic_tarihi).toISOString().split('T')[0];
        return lessonDate === today;
      });
      
      // Bu ayın derslerini filtrele
      const currentMonth = new Date().getMonth();
      const currentYear = new Date().getFullYear();
      
      const monthlyLessons = lessonsData.filter(lesson => {
        const lessonDate = new Date(lesson.baslangic_tarihi);
        return lessonDate.getMonth() === currentMonth && lessonDate.getFullYear() === currentYear;
      });
      
      setStats({
        totalLessons: lessonsData.length,
        totalStudents: studentsData.length,
        todayLessons: todayLessons.length,
        monthlyLessons: monthlyLessons.length
      });
      
    } catch (error) {
      console.error('Dashboard stats yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-8">
      <h1 className="text-3xl font-bold mb-8">Hoş Geldiniz, {teacherData?.adi}</h1>
      
      {/* Hakediş Özet Kartları */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="bg-gradient-to-r from-green-400 to-green-600 text-white p-6 rounded-lg shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-90 mb-1">Bu Ayki Hakediş</p>
              <p className="text-3xl font-bold">
                ₺{loading ? '...' : parseFloat(earnings.summary.bu_ay_hakedis || 0).toFixed(2)}
              </p>
              {!loading && earnings.summary.bu_ay_detay && (
                <div className="mt-2 text-sm opacity-90">
                  <p>Brüt: ₺{parseFloat(earnings.summary.bu_ay_detay.brut_toplam || 0).toFixed(2)}</p>
                  <p>Avans: -₺{parseFloat(earnings.summary.bu_ay_detay.avans || 0).toFixed(2)}</p>
                </div>
              )}
            </div>
            <div className="text-white opacity-80">
              <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          </div>
        </div>
        
        <div className="bg-gradient-to-r from-purple-400 to-purple-600 text-white p-6 rounded-lg shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-90 mb-1">Toplam Hakediş</p>
              <p className="text-3xl font-bold">
                ₺{loading ? '...' : parseFloat(earnings.summary.toplam_hakedis || 0).toFixed(2)}
              </p>
              {!loading && earnings.summary.toplam_detay && (
                <div className="mt-2 text-sm opacity-90">
                  <p>Brüt: ₺{parseFloat(earnings.summary.toplam_detay.brut_toplam || 0).toFixed(2)}</p>
                  <p>Avans: -₺{parseFloat(earnings.summary.toplam_detay.avans || 0).toFixed(2)}</p>
                </div>
              )}
            </div>
            <div className="text-white opacity-80">
              <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>
      </div>
      
      {/* Avans Bilgisi Kartı */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="bg-gradient-to-r from-orange-400 to-orange-600 text-white p-6 rounded-lg shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-90 mb-1">Geçen Ayki Hakediş</p>
              <p className="text-3xl font-bold">
                ₺{loading ? '...' : parseFloat(earnings.summary.gecen_ay_hakedis || 0).toFixed(2)}
              </p>
              {!loading && earnings.summary.gecen_ay_detay && (
                <div className="mt-2 text-sm opacity-90">
                  <p>Brüt: ₺{parseFloat(earnings.summary.gecen_ay_detay.brut_toplam || 0).toFixed(2)}</p>
                  <p>Avans: -₺{parseFloat(earnings.summary.gecen_ay_detay.avans || 0).toFixed(2)}</p>
                </div>
              )}
            </div>
            <div className="text-white opacity-80">
              <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </div>
          </div>
        </div>
        
        <div className="bg-gradient-to-r from-blue-400 to-blue-600 text-white p-6 rounded-lg shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-90 mb-1">Geçerli Avans Bakiyesi</p>
              <p className="text-3xl font-bold">
                ₺{loading ? '...' : parseFloat(earnings.summary.bu_ay_detay?.avans || 0).toFixed(2)}
              </p>
              <p className="text-xs opacity-75 mt-2">
                {parseFloat(earnings.summary.bu_ay_detay?.avans || 0) > 0 
                  ? 'Hakedişinizden düşülecek'
                  : 'Avans borcunuz bulunmuyor'}
              </p>
            </div>
            <div className="text-white opacity-80">
              <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
              </svg>
            </div>
          </div>
        </div>
      </div>
      
      {/* İstatistik Kartları */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-blue-500 text-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-90">Toplam Ders</p>
              <p className="text-3xl font-bold">
                {loading ? '...' : stats.totalLessons}
              </p>
            </div>
            <span className="text-4xl opacity-80">📚</span>
          </div>
        </div>
        
        <div className="bg-green-500 text-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-90">Öğrenci Sayısı</p>
              <p className="text-3xl font-bold">
                {loading ? '...' : stats.totalStudents}
              </p>
            </div>
            <span className="text-4xl opacity-80">👥</span>
          </div>
        </div>
        
        <div className="bg-purple-500 text-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-90">Bugünkü Ders</p>
              <p className="text-3xl font-bold">
                {loading ? '...' : stats.todayLessons}
              </p>
            </div>
            <span className="text-4xl opacity-80">📅</span>
          </div>
        </div>
        
        <div className="bg-orange-500 text-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-90">Bu Ay</p>
              <p className="text-3xl font-bold">
                {loading ? '...' : stats.monthlyLessons}
              </p>
            </div>
            <span className="text-4xl opacity-80">📊</span>
          </div>
        </div>
      </div>
      
      {/* Kişisel Bilgiler Kartı */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg shadow p-6 mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-800">Kişisel Bilgilerim</h2>
          <button 
            onClick={() => setCurrentPage('profile')}
            className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
          >
            Profili Düzenle
            <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white bg-opacity-70 rounded p-3">
            <p className="text-xs text-gray-600">Ad Soyad</p>
            <p className="font-medium text-gray-900">{teacherData?.adi || '-'}</p>
          </div>
          <div className="bg-white bg-opacity-70 rounded p-3">
            <p className="text-xs text-gray-600">E-posta</p>
            <p className="font-medium text-gray-900">{teacherData?.e_posta || '-'}</p>
          </div>
          <div className="bg-white bg-opacity-70 rounded p-3">
            <p className="text-xs text-gray-600">Telefon</p>
            <p className="font-medium text-gray-900">{teacherData?.telefon || '-'}</p>
          </div>
          <div className="bg-white bg-opacity-70 rounded p-3">
            <p className="text-xs text-gray-600">İkinci Telefon</p>
            <p className="font-medium text-gray-900">{teacherData?.teltwo || '-'}</p>
          </div>
          <div className="bg-white bg-opacity-70 rounded p-3">
            <p className="text-xs text-gray-600">IBAN</p>
            <p className="font-medium text-gray-900">{teacherData?.iban || '-'}</p>
          </div>
          <div className="bg-white bg-opacity-70 rounded p-3">
            <p className="text-xs text-gray-600">Adres</p>
            <p className="font-medium text-gray-900">{teacherData?.adres || '-'}</p>
          </div>
        </div>
      </div>

      {/* Hızlı Erişim Butonları */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <button
          onClick={() => setCurrentPage('students')}
          className="bg-white p-6 rounded-lg shadow hover:shadow-md transition-shadow text-center group"
        >
          <span className="text-3xl mb-2 block group-hover:scale-110 transition-transform">👥</span>
          <span className="text-base font-medium">Öğrencilerim</span>
          <span className="text-xs text-gray-500 block mt-1">
            {loading ? '...' : `${stats.totalStudents} öğrenci`}
          </span>
        </button>
        <button
          onClick={() => setCurrentPage('lessons')}
          className="bg-white p-6 rounded-lg shadow hover:shadow-md transition-shadow text-center group"
        >
          <span className="text-3xl mb-2 block group-hover:scale-110 transition-transform">📚</span>
          <span className="text-base font-medium">Derslerim</span>
          <span className="text-xs text-gray-500 block mt-1">
            {loading ? '...' : `${stats.totalLessons} ders`}
          </span>
        </button>
        <button
          onClick={() => setCurrentPage('profile')}
          className="bg-white p-6 rounded-lg shadow hover:shadow-md transition-shadow text-center group"
        >
          <span className="text-3xl mb-2 block group-hover:scale-110 transition-transform">👤</span>
          <span className="text-base font-medium">Profilim</span>
        </button>
        <button
          onClick={() => setCurrentPage('settings')}
          className="bg-white p-6 rounded-lg shadow hover:shadow-md transition-shadow text-center group"
        >
          <span className="text-3xl mb-2 block group-hover:scale-110 transition-transform">⚙️</span>
          <span className="text-base font-medium">Ayarlar</span>
        </button>
      </div>
      
      {/* Genel Bilgi Kartı */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold mb-4">Hesap Bilgileri</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="border-l-4 border-blue-500 pl-4">
            <p className="text-sm text-gray-600">Verdiğim Dersler</p>
            <p className="text-2xl font-bold text-blue-600">
              {loading ? '...' : stats.totalLessons}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Toplam ders sayısı
            </p>
          </div>
          <div className="border-l-4 border-green-500 pl-4">
            <p className="text-sm text-gray-600">Kayıt Durumu</p>
            <p className="text-lg font-medium text-green-600">Aktif</p>
          </div>
        </div>
      </div>
    </div>
  );
}